import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/ui/date-picker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Receipt, Loader2, Euro, CalendarCheck, Wrench, Moon, Home as HomeIcon, Wallet, Clock } from "lucide-react";
import { CashToggle } from "@/components/CashToggle";
import { NightFeeEditor } from "@/components/NightFeeEditor";
import { formatDate } from "@/lib/date";
import { toast } from "sonner";

import { tx, lz } from "@/i18n";
const euro = (v) => Number(v || 0).toLocaleString("nl-NL", { style: "currency", currency: "EUR" });
const monthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const monthEnd = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10); };

export default function Billing() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [houses, setHouses] = useState([]);
  const [houseId, setHouseId] = useState("");
  const [start, setStart] = useState(monthStart);
  const [end, setEnd] = useState(monthEnd);
  const [data, setData] = useState(null);
  const [allHours, setAllHours] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/houses").then(({ data }) => {
      setHouses(data);
      if (data.length) setHouseId(data[0].id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isAdmin || !start || !end) return;
    api.get("/billing/hours-by-worker", { params: { start, end } })
      .then(({ data }) => setAllHours(data)).catch(() => setAllHours(null));
  }, [isAdmin, start, end]);

  const run = async () => {
    if (!houseId || !start || !end) return;
    setLoading(true);
    try {
      const { data } = await api.get("/billing/overview", { params: { house_id: houseId, start, end } });
      setData(data);
    } catch (err) { toast.error(apiError(err)); setData(null); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (houseId && start && end) run(); /* eslint-disable-next-line */ }, [houseId, start, end]);

  const renderBookingRow = (b) => (
    <div key={b.id} className="px-5 py-3 flex items-center justify-between gap-4 text-sm" data-testid={`billing-booking-${b.id}`}>
      <div className="min-w-0">
        <div className="font-medium truncate">{b.guest_name || "—"}</div>
        <div className="text-muted-foreground text-xs flex items-center gap-2 flex-wrap mt-0.5">
          <span>{formatDate(b.arrival)} → {formatDate(b.departure)}</span>
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${b.stay_type === "eigen_verblijf" ? "bg-secondary/15 text-secondary" : "bg-primary/10 text-primary"}`}>{b.rate_label}</span>
        </div>
      </div>
      <span className="font-semibold shrink-0">{euro(b.amount)}</span>
    </div>
  );
  const renderExtraRow = (e) => (
    <div key={e.id} className="px-5 py-3 flex items-center justify-between gap-4 text-sm" data-testid={`billing-extra-${e.id}`}>
      <div className="min-w-0">
        <div className={`font-medium truncate flex items-center gap-2 ${e.waived ? "line-through text-muted-foreground" : ""}`}>{e.kind === "night" ? <Moon className="h-3.5 w-3.5 text-secondary shrink-0" /> : <Wrench className="h-3.5 w-3.5 text-muted-foreground shrink-0" />} {e.title}</div>
        <div className="text-muted-foreground text-xs mt-0.5">{formatDate(e.date)}</div>
      </div>
      <span className="shrink-0 flex items-center gap-1.5">
        {e.waived ? <span className="text-xs font-semibold text-muted-foreground" data-testid={`night-fee-waived-${e.booking_id}`}>{tx("Vervallen")}</span>
          : <span className="font-semibold">{euro(e.amount)}</span>}
        {isAdmin && e.kind === "night" && <NightFeeEditor item={e} onSaved={run} />}
      </span>
    </div>
  );
  const renderCostCard = (bookings, extras, withDot, key) => {
    if (!bookings.length && !extras.length) return null;
    return (
      <div key={key} className="bg-card rounded-xl border border-border shadow-sm overflow-hidden" data-testid={withDot ? "billing-bookings-cash" : "billing-bookings-regular"}>
        <div className="px-5 py-3 border-b border-border flex items-center gap-2 font-display font-semibold">
          <CalendarCheck className="h-4 w-4 text-secondary" /> {isAdmin ? tx("Boekingen in periode") : tx("Kosten in periode")}
          {withDot && <CashToggle value canEdit={false} testId="billing-cash-card-marker" />}
        </div>
        <div className="divide-y divide-border">
          {bookings.map(renderBookingRow)}
          {extras.map(renderExtraRow)}
        </div>
      </div>
    );
  };

  return (
    <div className="animate-fade-up">
      <div className="mb-8">
        <h1 className="font-display font-bold text-3xl tracking-tight flex items-center gap-2"><Receipt className="h-7 w-7 text-primary" /> {isAdmin ? tx("Kosten & Facturatie") : tx("Overzicht")}</h1>
        {isAdmin && <p className="text-muted-foreground mt-1">{tx("Totaaltelling per woning over een gekozen periode — standaardtarief per boeking plus ad-hoc extra kosten.")}</p>}
      </div>

      <div className="bg-card rounded-xl border border-border p-5 shadow-sm grid gap-4 md:grid-cols-3 mb-6" data-testid="billing-filters">
        <div>
          <Label className="text-xs">{tx("Woning")}</Label>
          <Select value={houseId} onValueChange={setHouseId}>
            <SelectTrigger className="mt-1" data-testid="billing-house-select"><SelectValue placeholder={tx("Kies woning")} /></SelectTrigger>
            <SelectContent>{houses.map((h) => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">{tx("Van (aankomstdatum)")}</Label>
          <DatePicker value={start} onChange={setStart} className="mt-1" testId="billing-start-input" />
        </div>
        <div>
          <Label className="text-xs">{tx("Tot en met")}</Label>
          <DatePicker value={end} onChange={setEnd} className="mt-1" testId="billing-end-input" />
        </div>
      </div>

      {isAdmin && allHours && allHours.workers.length > 0 && (
        <div className="bg-card rounded-xl border border-border shadow-sm overflow-hidden mb-6" data-testid="billing-all-hours">
          <div className="px-5 py-3 border-b border-border flex items-center gap-2 font-display font-semibold">
            <Clock className="h-4 w-4 text-secondary" /> {tx("Uren per medewerker — alle woningen")}
            <span className="text-xs font-normal text-muted-foreground ml-1">{tx("(hele bedrijf, gekozen periode)")}</span>
          </div>
          <div className="divide-y divide-border">
            {allHours.workers.map((w) => (
              <div key={w.worker_id || w.name} className="px-5 py-3 text-sm" data-testid={`billing-all-worker-${w.worker_id || "unknown"}`}>
                <div className="flex items-center justify-between gap-4">
                  <span className="font-medium">{w.name}</span>
                  <span className="font-semibold">{w.total.hours} u · {euro(w.total.amount)}</span>
                </div>
                <div className="mt-1 flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                  <span className="flex items-center gap-1.5"><CashToggle value canEdit={false} /> {tx("Cash:")} {w.cash.hours} u · {euro(w.cash.amount)}</span>
                  <span>{tx("Factuur:")} {w.invoice.hours} u · {euro(w.invoice.amount)}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="px-5 py-3 border-t border-border flex items-center justify-between text-sm font-semibold" data-testid="billing-all-hours-total">
            <span>{tx("Totaal (alle medewerkers)")}</span>
            <span>{allHours.totals.total.hours} u · {euro(allHours.totals.total.amount)}</span>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
      ) : !data ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center text-muted-foreground" data-testid="billing-empty">
          <Receipt className="h-10 w-10 mx-auto mb-3 opacity-40" /> {tx("Kies een woning en periode om het kostenoverzicht te tonen.")}
        </div>
      ) : (
        <div className="space-y-6" data-testid="billing-overview">
          {/* Totals */}
          {isAdmin && (
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="bg-card rounded-xl border border-border p-5 shadow-sm" data-testid="billing-total-bookings">
              <div className="flex items-center gap-2 text-muted-foreground text-sm"><CalendarCheck className="h-4 w-4" /> {tx("Boekingen")}</div>
              <div className="text-2xl font-bold mt-1">{euro(data.totals.bookings_total)}</div>
            </div>
            <div className="bg-card rounded-xl border border-border p-5 shadow-sm" data-testid="billing-total-extra">
              <div className="flex items-center gap-2 text-muted-foreground text-sm"><Wrench className="h-4 w-4" /> {tx("Extra kosten")}</div>
              <div className="text-2xl font-bold mt-1">{euro(data.totals.extra_total)}</div>
            </div>
            <div className="bg-primary/5 rounded-xl border-2 border-primary/30 p-5 shadow-sm" data-testid="billing-total-grand">
              <div className="flex items-center gap-2 text-primary text-sm font-semibold"><Euro className="h-4 w-4" /> {tx("Totaal te factureren")}</div>
              <div className="text-2xl font-bold mt-1 text-primary">{euro(data.totals.grand_total)}</div>
            </div>
          </div>
          )}

          {/* Boekingen in periode (incl. ad-hoc kosten) — gesplitst: zonder cash-bolletje en met cash-bolletje */}
          {renderCostCard(data.bookings.filter((b) => !b.cash), data.extra_costs.filter((e) => !e.cash), false, "regular")}
          {renderCostCard(data.bookings.filter((b) => b.cash), data.extra_costs.filter((e) => e.cash), true, "cash")}
          {data.bookings.length === 0 && data.extra_costs.length === 0 && (
            <div className="bg-card rounded-xl border border-border shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-border flex items-center gap-2 font-display font-semibold"><CalendarCheck className="h-4 w-4 text-secondary" /> {isAdmin ? tx("Boekingen in periode") : tx("Kosten in periode")}</div>
              <p className="px-5 py-6 text-sm text-muted-foreground" data-testid="billing-no-bookings">{tx("Geen boekingen of kosten in deze periode.")}</p>
            </div>
          )}

          {/* Loonkosten (uitgaande kosten) — admin only */}
          {isAdmin && (
            <div className="bg-card rounded-xl border border-border shadow-sm overflow-hidden" data-testid="billing-labour">
              <div className="px-5 py-3 border-b border-border flex items-center gap-2 font-display font-semibold"><Wallet className="h-4 w-4 text-rose-600" /> {tx("Uitgaande kosten — Loonkosten")}</div>
              {data.labour_costs.length === 0 ? (
                <p className="px-5 py-6 text-sm text-muted-foreground" data-testid="billing-no-labour">{tx("Geen gewerkte uren geregistreerd in deze periode.")}</p>
              ) : (
                <>
                  <div className="divide-y divide-border">
                    {data.labour_costs.map((l) => (
                      <div key={l.id} className="px-5 py-3 flex items-center justify-between gap-4 text-sm" data-testid={`billing-labour-${l.id}`}>
                        <div className="min-w-0">
                          <div className="font-medium truncate flex items-center gap-2">{l.title}{l.cash && <CashToggle value canEdit={false} />}</div>
                          <div className="text-muted-foreground text-xs mt-0.5">{formatDate(l.date)} · {l.assignee_name || "—"} · {l.hours} u × {euro(l.hourly_rate)}</div>
                        </div>
                        <div className="font-semibold shrink-0 text-rose-700">{euro(l.amount)}</div>
                      </div>
                    ))}
                  </div>
                  <div className="px-5 py-3 border-t border-border flex items-center justify-between text-sm font-semibold" data-testid="billing-labour-total">
                    <span>{tx("Totaal loonkosten")}</span><span className="text-rose-700">{euro(data.totals.labour_total)}</span>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Gewerkte uren overzicht — admin only */}
          {isAdmin && (
            <div className="bg-card rounded-xl border border-border shadow-sm overflow-hidden" data-testid="billing-hours">
              <div className="px-5 py-3 border-b border-border flex items-center gap-2 font-display font-semibold"><Clock className="h-4 w-4 text-secondary" /> {tx("Gewerkte uren")}</div>
              {(data.hours_by_worker || []).length > 0 && (
                <div className="divide-y divide-border border-b border-border" data-testid="billing-hours-by-worker">
                  {data.hours_by_worker.map((w) => (
                    <div key={w.worker_id || w.name} className="px-5 py-3 text-sm" data-testid={`billing-worker-${w.worker_id || "unknown"}`}>
                      <div className="flex items-center justify-between gap-4">
                        <span className="font-medium">{w.name}</span>
                        <span className="font-semibold">{w.total.hours} u · {euro(w.total.amount)}</span>
                      </div>
                      <div className="mt-1 flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                        <span className="flex items-center gap-1.5"><CashToggle value canEdit={false} /> {tx("Cash:")} {w.cash.hours} u · {euro(w.cash.amount)}</span>
                        <span>{tx("Factuur:")} {w.invoice.hours} u · {euro(w.invoice.amount)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="divide-y divide-border">
                <div className="px-5 py-3 flex items-center justify-between gap-4 text-sm" data-testid="billing-hours-cash">
                  <span className="flex items-center gap-2"><CashToggle value canEdit={false} /> {tx("Cash-uren")}</span>
                  <span className="font-medium">{data.hours_overview.cash.hours} u · {euro(data.hours_overview.cash.amount)}</span>
                </div>
                <div className="px-5 py-3 flex items-center justify-between gap-4 text-sm" data-testid="billing-hours-invoice">
                  <span>{tx("Factuur-uren")}</span>
                  <span className="font-medium">{data.hours_overview.invoice.hours} u · {euro(data.hours_overview.invoice.amount)}</span>
                </div>
              </div>
              <div className="px-5 py-3 border-t border-border flex items-center justify-between text-sm font-semibold" data-testid="billing-hours-total">
                <span>{tx("Totaal")}</span><span>{data.hours_overview.total.hours} u · {euro(data.hours_overview.total.amount)}</span>
              </div>
            </div>
          )}

          {isAdmin && (
          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
            <HomeIcon className="h-3.5 w-3.5" /> {tx("Standaardtarieven: Totaalpakket")} {euro(data.rates.standaard_pakket)} {tx("· Schoonmaak + linnen")} {euro(data.rates.schoonmaak_linnen)} {tx("(in te stellen op de woningdetailpagina).")}
          </p>
          )}
        </div>
      )}
    </div>
  );
}
